import { useState, useEffect, useCallback } from "react";
import { TEMPLATES, DEFAULT_WIDGET_CONFIG } from "./constants";
import mainicon from "../../assets/main-icon.svg";

const RotatingMessage = ({ messages, interval }: any) => {
  const [idx, setIdx] = useState(0);
  const [fade, setFade] = useState(true);
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const timer = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIdx((prev) => (prev + 1) % messages.length);
        setFade(true);
      }, 300);
    }, (interval || 4) * 1000);
    return () => clearInterval(timer);
  }, [messages, interval]);
  if (!messages || messages.length === 0) return null;
  return (
    <span className={`wc-lp-rotating-msg ${fade ? "wc-lp-rotating-msg--visible" : ""}`}>
      {messages[idx]}
    </span>
  );
};

const StackedMessages = ({ messages, primaryColor, textColor, onClickMessage }: any) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const allVisible = visibleCount >= (messages?.length || 0);

  useEffect(() => {
    if (!messages || messages.length === 0) return;
    setVisibleCount(0);
    let cancelled = false;

    // Show messages one by one, then keep them all visible (no reset)
    messages.forEach((_, i) => {
      setTimeout(() => {
        if (!cancelled) setVisibleCount(i + 1);
      }, (i + 1) * 800);
    });

    return () => { cancelled = true; };
  }, [messages]);

  if (!messages || messages.length === 0) return null;

  return (
    <div className={`wc-lp-stacked-bubbles ${allVisible ? "wc-lp-stacked-bubbles--pulse" : ""}`}>
      {messages.slice(0, visibleCount).map((msg, i) => (
        <div
          key={i}
          className="wc-lp-stacked-bubble wc-lp-stacked-bubble--enter"
          style={{ backgroundColor: primaryColor, color: textColor, cursor: "pointer" }}
          onClick={onClickMessage}
        >
          <span className="wc-lp-stacked-bubble-text">{msg}</span>
        </div>
      ))}
    </div>
  );
};

const LivePreview = ({ config, chatIcon }: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const [animKey, setAnimKey] = useState(0);

  const appearance = config.appearance || {};
  const welcome = config.welcome || {};
  const branding = config.branding || {};
  const animation = config.animation || {};
  const behavior = config.behavior || {};
  const template = config.template || "floating";

  const secondaryColor = appearance.secondaryColor || "#e1bee7";
  const borderRadius = appearance.borderRadius ?? 12;
  const fontFamily = appearance.fontFamily || "Nunito";
  const widgetSize = appearance.widgetSize ?? 60;

  // Chat theme colors (from Appearance tab)
  const defaultTheme = DEFAULT_WIDGET_CONFIG.chatTheme;
  const ct = config.chatTheme || {};
  const headerBg = ct.chatprimaryBg || defaultTheme.chatprimaryBg;
  const headerText = ct.chatprimaryText || defaultTheme.chatprimaryText;
  const botBg = ct.botBg || defaultTheme.botBg;
  const botTextColor = ct.botText || defaultTheme.botText;
  const chatBgColor = ct.chatBgColor || defaultTheme.chatBgColor;
  const submenuBg = ct.submenuBg || defaultTheme.submenuBg;
  const submenuHoverBg = ct.submenuHoverBg || defaultTheme.submenuHoverBg;
  const submenuTextColor = ct.submenuText || defaultTheme.submenuText;
  const logoBgColor = ct.logoBgColor || defaultTheme.logoBgColor;

  // Check if current template supports animation
  const selectedTemplate = TEMPLATES.find((t) => t.id === template);
  const supportsAnimation = selectedTemplate?.features?.animation ?? false;

  // Icon animations (FAB / small circular elements)
  const getIconAnimationClass = useCallback(() => {
    if (!animation.enabled || !supportsAnimation) return "";
    switch (animation.type) {
      case "bounce": return "wc-anim-bounce";
      case "pulse": return "wc-anim-pulse";
      case "floating": return "wc-anim-floating";
      case "slideIn": return "wc-anim-slideIn";
      case "fadeIn": return "wc-anim-fadeIn";
      case "zoomIn": return "wc-anim-zoomIn";
      case "wave": return "wc-anim-wave";
      case "glow": return "wc-anim-glow";
      default: return "";
    }
  }, [animation.enabled, animation.type, supportsAnimation]);

  // Card animations (Assistant Bubble, Concierge — gentler, no scale jitter)
  const getCardAnimationClass = useCallback(() => {
    if (!animation.enabled || !supportsAnimation) return "";
    switch (animation.type) {
      case "bounce": return "wc-anim-card-bounce";
      case "pulse": return "wc-anim-card-pulse";
      case "floating": return "wc-anim-card-floating";
      case "slideIn": return "wc-anim-card-slideIn";
      case "fadeIn": return "wc-anim-card-fadeIn";
      case "zoomIn": return "wc-anim-card-zoomIn";
      case "wave": return "wc-anim-card-wave";
      case "glow": return "wc-anim-card-glow";
      default: return "";
    }
  }, [animation.enabled, animation.type, supportsAnimation]);

  // Replay animation on frequency interval
  useEffect(() => {
    if (!animation.enabled || !supportsAnimation) return;
    const freq = animation.frequency;
    const oneShot = ["slideIn", "fadeIn", "zoomIn"];
    const isOneShot = oneShot.includes(animation.type);

    // For one-shot animations (slideIn/fadeIn/zoomIn):
    // "continuous" means replay every 30s (not CSS infinite since they use forwards)
    // For looping animations (pulse/bounce/wave/glow/floating):
    // "continuous" uses CSS infinite, no JS timer needed
    if (!freq || freq === "continuous") {
      if (isOneShot) {
        const timer = setInterval(() => setAnimKey((k) => k + 1), 30000);
        return () => clearInterval(timer);
      }
      return;
    }

    const intervalMs =
      freq === "5s" ? 5000 :
      freq === "10s" ? 10000 :
      freq === "15s" ? 15000 :
      freq === "30s" ? 30000 :
      freq === "60s" ? 60000 : 0;

    if (intervalMs === 0) return;

    const timer = setInterval(() => {
      setAnimKey((k) => k + 1);
    }, intervalMs);

    return () => clearInterval(timer);
  }, [animation.enabled, animation.frequency, animation.type, supportsAnimation]);

  // Get animation iteration count style based on frequency
  const getAnimationStyle = () => {
    if (!animation.enabled || !supportsAnimation) return {};
    const freq = animation.frequency;
    if (!freq || freq === "continuous") return {}; // uses CSS infinite
    // Slide In, Fade In, Zoom In: play once per trigger (non-distracting)
    const oneShot = ["slideIn", "fadeIn", "zoomIn"];
    if (oneShot.includes(animation.type)) {
      return { animationIterationCount: 1 };
    }
    // Pulse, Bounce, Floating, Wave, Glow: play 2 times per trigger
    return { animationIterationCount: 2 };
  };

  const templateContent = config.templateContent || {};
  // Resolve display name: prefer template-specific title for concierge/assistant templates
  const conciergeContent = templateContent.concierge || {};
  const assistantContent = templateContent.assistant_bubble || {};
  const baseAgentName = branding.agentName || "AI Concierge";
  const agentName =
    template === "concierge" && conciergeContent.title ? conciergeContent.title :
    template === "assistant_bubble" && assistantContent.agentName ? assistantContent.agentName :
    baseAgentName;
  const avatarSrc = branding.agentAvatar || appearance.avatarImage || chatIcon || mainicon;
  const logoSrc = appearance.hotelLogo || chatIcon || mainicon;
  const floatingContent = templateContent.floating || {};
  const floatingMessages = Array.isArray(floatingContent.messages)
    ? floatingContent.messages
    : welcome.typingMessages || ["Looking for rooms?", "Book direct & save!", "AI assistant at your service"];
  const floatingDisplayStyle = floatingContent.displayStyle || "rotating";

  const [hoveredChipIdx, setHoveredChipIdx] = useState<any>(null);

  const renderChatWindow = () => (
    <div className="wc-chat-preview" style={{ fontFamily }}>
      {/* Header — matches production ChatHeader */}
      <div
        className="wc-chat-preview-header"
        style={{ backgroundColor: headerBg, color: headerText }}
      >
        <div className="wc-chat-preview-header-inner">
          <div className="wc-chat-preview-logo" style={{ backgroundColor: logoBgColor }}>
            <img src={logoSrc} alt="Logo" />
          </div>
          <div className="wc-chat-preview-header-titles">
            <span className="wc-chat-preview-botname">{agentName}</span>
            <span className="wc-chat-preview-propname">Hotel Property</span>
          </div>
        </div>
        <div className="wc-chat-preview-header-actions">
          <span className="wc-chat-preview-header-btn"><i className="bi bi-arrows-fullscreen" /></span>
          <span className="wc-chat-preview-header-btn" onClick={() => setIsOpen(false)} style={{ cursor: "pointer" }}>
            <i className="bi bi-x-lg" />
          </span>
        </div>
      </div>

      {/* Body — matches production MessageList / MessageBubble */}
      <div className="wc-chat-preview-body" style={{ backgroundColor: chatBgColor }}>
        {/* Bot message */}
        <div className="wc-chat-preview-msg-row wc-chat-preview-msg-row--bot">
          <div className="wc-chat-preview-avatar" style={{ backgroundColor: headerBg }}>
            {chatIcon
              ? <img src={chatIcon} alt="Bot" className="wc-chat-preview-avatar-img" />
              : <span style={{ color: "#fff", fontSize: "10px", fontWeight: 700 }}>AI</span>
            }
          </div>
          <div>
            <div
              className="wc-chat-preview-bubble wc-chat-preview-bubble--bot"
              style={{ backgroundColor: botBg, color: botTextColor }}
            >
              {template === "concierge" && conciergeContent.greeting
                ? conciergeContent.greeting
                : welcome.subtitle || "Welcome! How can I assist you today?"}
            </div>
            <span className="wc-chat-preview-time">Just now</span>
          </div>
        </div>

        {/* Submenu chips */}
        <div className="wc-chat-preview-submenu">
          {["Book Now", "Contact Us", "Talk to Agent", "Cancel/Modify"].map((text, idx) => (
            <span
              key={idx}
              className="wc-chat-preview-chip"
              onMouseEnter={() => setHoveredChipIdx(idx)}
              onMouseLeave={() => setHoveredChipIdx(null)}
              style={{
                backgroundColor: hoveredChipIdx === idx ? submenuHoverBg : submenuBg,
                color: submenuTextColor,
              }}
            >
              {text}
            </span>
          ))}
        </div>

        {/* User message */}
        <div className="wc-chat-preview-msg-row wc-chat-preview-msg-row--user">
          <div
            className="wc-chat-preview-bubble wc-chat-preview-bubble--user"
            style={{ backgroundColor: headerBg, color: headerText }}
          >
            Hi there!
          </div>
        </div>

        {/* Bot reply */}
        <div className="wc-chat-preview-msg-row wc-chat-preview-msg-row--bot">
          <div className="wc-chat-preview-avatar" style={{ backgroundColor: headerBg }}>
            {chatIcon
              ? <img src={chatIcon} alt="Bot" className="wc-chat-preview-avatar-img" />
              : <span style={{ color: "#fff", fontSize: "10px", fontWeight: 700 }}>AI</span>
            }
          </div>
          <div>
            <div
              className="wc-chat-preview-bubble wc-chat-preview-bubble--bot"
              style={{ backgroundColor: botBg, color: botTextColor }}
            >
              Hello! How can I help you?
            </div>
            <span className="wc-chat-preview-time">Just now</span>
          </div>
        </div>

      </div>

      {/* Input — matches production ChatInput */}
      <div className="wc-chat-preview-input">
        <input type="text" placeholder="Type here..." readOnly />
        <span
          className="wc-chat-preview-send"
          style={{ backgroundColor: headerBg, color: headerText }}
        >
          <i className="bi bi-send-fill" />
        </span>
      </div>
    </div>
  );

  /* ===== Template 1: Floating Icon + Rotating Message + Badge ===== */
  const renderFloatingIcon = () => (
    <div className="wc-lp-floating-wrapper">
      {floatingDisplayStyle === "stacked" ? (
        <StackedMessages messages={floatingMessages} primaryColor={headerBg} textColor={headerText} onClickMessage={() => setIsOpen(true)} />
      ) : (
        <div className="wc-lp-rotating-bubble wc-lp-rotating-bubble--attention" style={{ backgroundColor: headerBg, color: headerText, borderColor: headerBg, cursor: "pointer" }} onClick={() => setIsOpen(true)}>
          <RotatingMessage messages={floatingMessages} interval={4} />
        </div>
      )}
      <div className="wc-lp-fab-group">
        <div
          key={animKey}
          className={`wc-lp-fab ${getIconAnimationClass()}`}
          style={{
            width: `${widgetSize}px`,
            height: `${widgetSize}px`,
            backgroundColor: logoBgColor,
            borderRadius: "50%",
            ...getAnimationStyle(),
          }}
          onClick={() => setIsOpen(true)}
        >
          {chatIcon
            ? <img src={chatIcon} alt="Chat" style={{ width: "60%", height: "60%", borderRadius: "50%" }} />
            : <i className="bi bi-chat-dots-fill" style={{ color: "#333", fontSize: `${widgetSize * 0.4}px` }} />
          }
        </div>
        {behavior.showUnreadBadge && behavior.notificationCount > 0 && (
          <span className="wc-lp-notif-badge" style={{ backgroundColor: "#ef4444" }}>
            {behavior.notificationCount}
          </span>
        )}
      </div>
    </div>
  );

  /* ===== Template 2: Promotional Card (offers/discounts focused) ===== */
  const promoContent = templateContent.promo_card || {};
  const renderPromoCard = () => (
    <div className="wc-lp-template-with-fab wc-lp-template-with-fab--left">
      <div
        key={animKey}
        className={`wc-lp-promo-card ${supportsAnimation && animation.enabled ? getCardAnimationClass() : ""}`}
        style={getAnimationStyle()}
        onClick={() => setIsOpen(true)}
      >
        <div className="wc-lp-promo-banner" style={{ backgroundColor: headerBg }}>
          {promoContent.title && <span className="wc-lp-promo-discount-tag">{promoContent.title}</span>}
        </div>
        <div className="wc-lp-promo-body">
          {promoContent.description && <p className="wc-lp-promo-desc">{promoContent.description}</p>}
          {promoContent.buttonText && (
            <button type="button"
              className="wc-lp-promo-cta"
              style={{ backgroundColor: headerBg, color: headerText }}
            >
              {promoContent.buttonText}
            </button>
          )}

        </div>
      </div>
      <div className="wc-lp-fab-group">
        <div
          className="wc-lp-fab"
          style={{
            width: `${widgetSize}px`,
            height: `${widgetSize}px`,
            backgroundColor: logoBgColor,
            borderRadius: "50%",
          }}
          onClick={() => setIsOpen(true)}
        >
          {chatIcon
            ? <img src={chatIcon} alt="Chat" style={{ width: "60%", height: "60%", borderRadius: "50%" }} />
            : <i className="bi bi-chat-dots-fill" style={{ color: "#333", fontSize: `${widgetSize * 0.4}px` }} />
          }
        </div>
        {behavior.showUnreadBadge && behavior.notificationCount > 0 && (
          <span className="wc-lp-notif-badge" style={{ backgroundColor: "#ef4444" }}>
            {behavior.notificationCount}
          </span>
        )}
      </div>
    </div>
  );

  /* ===== Template 3: Guest Relations Assistant (premium bubble) ===== */
  const renderAssistantBubble = () => (
    <div className="wc-lp-template-with-fab wc-lp-template-with-fab--top">
      <div
        className="wc-lp-assistant-v2"
        onClick={() => setIsOpen(true)}
      >
        <div
          key={animKey}
          className={`wc-lp-assistant-card ${getCardAnimationClass()}`}
          style={{ borderColor: headerBg + "20", ...getAnimationStyle() }}
        >
          <div className="wc-lp-assistant-identity">
            {assistantContent.agentName && (
              <span className="wc-lp-assistant-name" style={{ color: headerBg }}>
                {assistantContent.agentName}
              </span>
            )}
            {assistantContent.role && <span className="wc-lp-assistant-role">· {assistantContent.role}</span>}
          </div>
          {assistantContent.greeting && (
            <p className="wc-lp-assistant-greeting">{assistantContent.greeting}</p>
          )}
          {assistantContent.buttonText && (
            <button type="button"
              className="wc-lp-assistant-cta"
              style={{ backgroundColor: headerBg, color: headerText, borderRadius: `${borderRadius / 2}px` }}
            >
              {assistantContent.buttonText}
            </button>
          )}
        </div>
      </div>
      <div className="wc-lp-fab-group">
        <div
          className="wc-lp-fab"
          style={{
            width: `${widgetSize}px`,
            height: `${widgetSize}px`,
            backgroundColor: logoBgColor,
            borderRadius: "50%",
          }}
          onClick={() => setIsOpen(true)}
        >
          {chatIcon
            ? <img src={chatIcon} alt="Chat" style={{ width: "60%", height: "60%", borderRadius: "50%" }} />
            : <i className="bi bi-chat-dots-fill" style={{ color: "#333", fontSize: `${widgetSize * 0.4}px` }} />
          }
        </div>
        {behavior.showUnreadBadge && behavior.notificationCount > 0 && (
          <span className="wc-lp-notif-badge" style={{ backgroundColor: "#ef4444" }}>
            {behavior.notificationCount}
          </span>
        )}
      </div>
    </div>
  );

  /* ===== Template 4: Sticky Bar ===== */
  const stickyContent = templateContent.sticky_bar || {};
  const renderStickyBar = () => (
    <div className="wc-lp-floating-wrapper">
      <div
        key={animKey}
        className={`wc-lp-sticky-bar ${supportsAnimation && animation.enabled ? getCardAnimationClass() : ""}`}
        style={{ backgroundColor: headerBg, color: headerText, ...getAnimationStyle() }}
        onClick={() => setIsOpen(true)}
      >
        {stickyContent.message && <span>{stickyContent.message}</span>}
        {stickyContent.buttonText && <span className="wc-lp-sticky-cta">{stickyContent.buttonText}</span>}
      </div>
      <div className="wc-lp-fab-group">
        <div
          className="wc-lp-fab"
          style={{
            width: `${widgetSize}px`,
            height: `${widgetSize}px`,
            backgroundColor: logoBgColor,
            borderRadius: "50%",
          }}
          onClick={() => setIsOpen(true)}
        >
          {chatIcon
            ? <img src={chatIcon} alt="Chat" style={{ width: "60%", height: "60%", borderRadius: "50%" }} />
            : <i className="bi bi-chat-dots-fill" style={{ color: "#333", fontSize: `${widgetSize * 0.4}px` }} />
          }
        </div>
        {behavior.showUnreadBadge && behavior.notificationCount > 0 && (
          <span className="wc-lp-notif-badge" style={{ backgroundColor: "#ef4444" }}>
            {behavior.notificationCount}
          </span>
        )}
      </div>
    </div>
  );

  /* ===== Template 5: Premium Concierge (luxury style) ===== */
  const conciergeServices = Array.isArray(conciergeContent.services)
    ? conciergeContent.services
    : [];
  const renderConciergeCard = () => (
    <div className="wc-lp-template-with-fab wc-lp-template-with-fab--top">
      <div
        key={animKey}
        className={`wc-lp-concierge-v2 ${getCardAnimationClass()}`}
        style={getAnimationStyle()}
        onClick={() => setIsOpen(true)}
      >
        <div className="wc-lp-concierge-gold-bar" style={{ backgroundColor: secondaryColor }} />
        <div
          className="wc-lp-concierge-header"
          style={{ background: `linear-gradient(135deg, ${headerBg}, #1a1a2e)` }}
        >
          <div className="wc-lp-concierge-avatar-area">
            <div className="wc-lp-concierge-avatar-ring">
              <img src={avatarSrc} alt="Concierge" />
            </div>
            <span className="wc-lp-concierge-online-dot" />
          </div>
          <div className="wc-lp-concierge-header-text">
            {conciergeContent.title && <strong style={{ color: "#fff" }}>{conciergeContent.title}</strong>}
            {conciergeContent.subtitle && <span style={{ color: secondaryColor, fontSize: "9px" }}>{conciergeContent.subtitle}</span>}
          </div>
        </div>
        <div className="wc-lp-concierge-body">
          {conciergeContent.greeting && <p className="wc-lp-concierge-welcome">{conciergeContent.greeting}</p>}
          {conciergeServices.length > 0 && (
            <div className="wc-lp-concierge-services">
              {conciergeServices.map((svc, i) => (
                <span key={i} className="wc-lp-concierge-tag" style={{ borderColor: secondaryColor, color: headerBg }}>{svc}</span>
              ))}
            </div>
          )}
          <button type="button"
            className="wc-lp-concierge-btn"
            style={{ backgroundColor: headerBg, color: headerText }}
          >
            <i className="bi bi-bell-fill" style={{ fontSize: "10px", marginRight: "4px" }} />
            {conciergeContent.buttonText}
          </button>
        </div>
      </div>
      <div className="wc-lp-fab-group">
        <div
          className="wc-lp-fab"
          style={{
            width: `${widgetSize}px`,
            height: `${widgetSize}px`,
            backgroundColor: logoBgColor,
            borderRadius: "50%",
          }}
          onClick={() => setIsOpen(true)}
        >
          {chatIcon
            ? <img src={chatIcon} alt="Chat" style={{ width: "60%", height: "60%", borderRadius: "50%" }} />
            : <i className="bi bi-chat-dots-fill" style={{ color: "#333", fontSize: `${widgetSize * 0.4}px` }} />
          }
        </div>
        {behavior.showUnreadBadge && behavior.notificationCount > 0 && (
          <span className="wc-lp-notif-badge" style={{ backgroundColor: "#ef4444" }}>
            {behavior.notificationCount}
          </span>
        )}
      </div>
    </div>
  );

  const renderWidget = () => {
    if (isOpen) return renderChatWindow();
    switch (template) {
      case "sticky_bar": return renderStickyBar();
      case "assistant_bubble": return renderAssistantBubble();
      case "concierge": return renderConciergeCard();
      case "promo_card": return renderPromoCard();
      default: return renderFloatingIcon();
    }
  };

  return (
    <div className="wc-live-preview">
      <div className="wc-lp-toolbar">
        <h6 className="wc-lp-toolbar-title">
          <i className="bi bi-eye me-1" /> Live Preview
        </h6>
        <div className="wc-lp-toolbar-actions">
          <button type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setIsOpen(!isOpen)}
            title={isOpen ? "Close widget" : "Open widget"}
          >
            <i className={`bi ${isOpen ? "bi-chat-dots" : "bi-chat-dots-fill"}`} />
          </button>
        </div>
      </div>

      <div className="wc-lp-frame wc-lp-frame--desktop">
        <div className={`wc-lp-scene ${isOpen ? "wc-lp-scene--chat-open" : ""}`}>
          <div className="wc-lp-page-bg">
            <div className="wc-lp-page-line w-75" />
            <div className="wc-lp-page-line w-50" />
            <div className="wc-lp-page-line w-60" />
            <div className="wc-lp-page-line w-40" />
          </div>
          <div className="wc-lp-widget-area">
            {renderWidget()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LivePreview;
